
import React, { useState, useEffect } from 'react';
import { Rental } from '@/services/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, AlertTriangle, Database, CreditCard, Calendar, MapPin } from 'lucide-react';
import { getRentalDeletionStats, RentalDeletionStats } from '../services/rentalDeleteService';

interface DeleteRentalDialogProps {
  rental: Rental;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  loading: boolean;
}

export const DeleteRentalDialog: React.FC<DeleteRentalDialogProps> = ({
  rental,
  open,
  onOpenChange,
  onConfirm,
  loading
}) => {
  const [deletionStats, setDeletionStats] = useState<RentalDeletionStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [confirmDeletion, setConfirmDeletion] = useState(false);

  // Загружаем статистику связанных данных при открытии диалога
  useEffect(() => {
    if (open && rental) {
      loadDeletionStats();
    }
  }, [open, rental]);

  // Сбрасываем состояние при закрытии диалога
  useEffect(() => {
    if (!open) {
      setConfirmDeletion(false);
      setDeletionStats(null);
    }
  }, [open]);

  const loadDeletionStats = async () => {
    setStatsLoading(true);
    try {
      const stats = await getRentalDeletionStats(rental.id);
      setDeletionStats(stats);
    } catch (error) {
      console.error('Ошибка при загрузке статистики удаления:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const hasLinkedData = deletionStats && (
    deletionStats.trackingPoints > 0 || 
    deletionStats.payments > 0 || 
    deletionStats.subscriptions > 0
  );

  const handleConfirm = () => {
    if (hasLinkedData && !confirmDeletion) {
      return; // Не разрешаем удаление без подтверждения
    }
    onConfirm();
  };

  const handleCheckboxChange = (checked: boolean | "indeterminate") => {
    // Преобразуем CheckedState в boolean
    setConfirmDeletion(checked === true);
  };

  return (
    <AlertDialog open={open} onOpenChange={loading ? undefined : onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Удалить аренду
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>
                Вы уверены, что хотите удалить аренду{' '}
                <strong>{rental.human_number || rental.id.substring(0, 8)}</strong>?
              </p>

              {statsLoading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Проверяем связанные данные...
                </div>
              )}

              {deletionStats && hasLinkedData && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                    <Database className="h-4 w-4" />
                    Будут удалены связанные данные:
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    {deletionStats.trackingPoints > 0 && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span>{deletionStats.trackingPoints} GPS точек и данных пробега</span>
                      </div>
                    )}
                    
                    {deletionStats.payments > 0 && (
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-muted-foreground" />
                        <span>{deletionStats.payments} платежей</span>
                      </div>
                    )}
                    
                    {deletionStats.subscriptions > 0 && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>{deletionStats.subscriptions} подписок</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-start gap-2 mt-3">
                    <Checkbox
                      id="confirmDeletion"
                      checked={confirmDeletion}
                      onCheckedChange={handleCheckboxChange}
                      disabled={loading}
                    />
                    <label 
                      htmlFor="confirmDeletion" 
                      className="text-sm leading-5 cursor-pointer"
                    >
                      Я понимаю, что все связанные данные будут безвозвратно удалены
                    </label>
                  </div>
                </div>
              )}

              {deletionStats && !hasLinkedData && (
                <div className="bg-muted/50 border rounded-lg p-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Database className="h-4 w-4" />
                    Связанных данных не найдено
                  </div>
                </div>
              )}

              <p className="text-sm text-muted-foreground">
                Это действие нельзя отменить.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading || statsLoading}>
            Отмена
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            disabled={loading || statsLoading || (hasLinkedData && !confirmDeletion)}
            className="bg-destructive hover:bg-destructive/90"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Удаление...
              </>
            ) : (
              'Удалить'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
